import { InvalidInputError } from "./ByteBuffer.js";
import { ByteString } from "./ByteString.js";
class UInt64Constructor extends BigInt {
    static MAX_VALUE = 18446744073709551615n;
    static UINT64_TO_INT64_OFFSET = 9223372036854775808n;
    static MAX_BASE10_STR_LEN = this.MAX_VALUE.toString().length;
    static toInt64(val) {
        if (!this.isValid(val)) {
            throw new Error("Got invalid val", { cause: val });
        }
        return val - this.UINT64_TO_INT64_OFFSET;
    }
    static fromInt64(input) {
        const result = input + this.UINT64_TO_INT64_OFFSET;
        if (!this.isValid(result)) {
            throw new Error("Got invalid result", { cause: result });
        }
        return result;
    }
    static toPaddedString(val) {
        if (!this.isValid(val)) {
            throw new Error("Got invalid val", { cause: val });
        }
        return val.toString().padStart(UInt64.MAX_BASE10_STR_LEN, "0");
    }
    static fromPaddedString(input) {
        const result = BigInt(input);
        if (!this.isValid(result)) {
            throw new Error("Got invalid result", { cause: result });
        }
        return result;
    }
    static is(value) {
        return typeof value === "bigint";
    }
    static isValid(value) {
        const raw = value;
        return raw >= 0 && raw <= this.MAX_VALUE;
    }
    static async encodeCompactInline(input, consumer) {
        if (!this.isValid(input)) {
            throw new Error("Got invalid input", { cause: input });
        }
        const parts = this.toCompactEncodingComponents(input, 8);
        await consumer.write(parts.tag);
        await consumer.write(parts.additionalBytes);
    }
    static async decodeCompactInline(producer, canonic) {
        const tag = (await producer.read(1))[0];
        const additionalBytesLength = this.decodeCompactAdditionalBytesLength(tag, 8);
        const value = additionalBytesLength > 0
            ? this.decodeCompactAdditionalBytes(await producer.read(additionalBytesLength))
            : BigInt(tag);
        if (canonic) {
            const expectedTag = this.toCompactEncodingComponents(value, 8).tag;
            if (tag !== expectedTag) {
                throw new InvalidInputError("Tag is not minimal for value", {
                    cause: {
                        tag,
                        value,
                        expectedTag,
                    },
                });
            }
        }
        if (!this.isValid(value)) {
            throw new Error("Got invalid result", { cause: value });
        }
        return value;
    }
    static encodeCompact(val, headerByte, tagWidth, bitBigEndianPosition) {
        if (!this.isValid(val)) {
            throw new Error("Got invalid val", { cause: val });
        }
        if (tagWidth < 2 || tagWidth > 8) {
            throw new Error("tagWidth must be between 2 and 8 included", {
                cause: tagWidth,
            });
        }
        if (bitBigEndianPosition < 0 || bitBigEndianPosition > 7) {
            throw new Error("bitBigEndianPosition must be between 0 and 7 included", {
                cause: bitBigEndianPosition,
            });
        }
        const { tag, additionalBytes } = this.toCompactEncodingComponents(val, tagWidth);
        const headerResult = headerByte | (tag << (8 - (bitBigEndianPosition + tagWidth)));
        return {
            headerByte: headerResult,
            additionalBytes,
        };
    }
    static async decodeCompact(headerByte, tagWidth, bitBigEndianPosition, producer, canonic) {
        if (tagWidth < 2 || tagWidth > 8) {
            throw new Error("tagWidth must be between 2 and 8 included", {
                cause: tagWidth,
            });
        }
        if (bitBigEndianPosition < 0 || bitBigEndianPosition > 7) {
            throw new Error("bitBigEndianPosition must be between 0 and 7 included", {
                cause: bitBigEndianPosition,
            });
        }
        const mask = 2 ** tagWidth - 1;
        const tag = (headerByte >> (8 - (bitBigEndianPosition + tagWidth))) & mask;
        let value = BigInt(tag);
        const additionalBytesLength = this.decodeCompactAdditionalBytesLength(tag, tagWidth);
        if (additionalBytesLength > 0) {
            value = this.decodeCompactAdditionalBytes(await producer.read(additionalBytesLength));
        }
        if (canonic) {
            const expectedTag = this.toCompactEncodingComponents(value, tagWidth).tag;
            if (tag !== expectedTag) {
                throw new InvalidInputError("Tag is not minimal for value", {
                    cause: {
                        tag,
                        value,
                        expectedTag,
                    },
                });
            }
        }
        if (!this.isValid(value)) {
            throw new Error("Got invalid result", { cause: value });
        }
        return value;
    }
    static toCompactEncodingComponents(input, tagWidth) {
        if (!this.isValid(input)) {
            throw new Error("Got invalid input", { cause: input });
        }
        if (tagWidth < 2) {
            throw new Error("tagWidth must be at least 2");
        }
        if (!this.isValid(input)) {
            throw new Error("input must be a valid 64-bit unsigned integer");
        }
        const n = input;
        if (n < 2 ** tagWidth - 4) {
            return {
                tag: Number(n),
                additionalBytes: ByteString.empty(),
            };
        }
        const bytes = new Uint8Array(Array(8)
            .fill(0)
            .map((_, index) => Number((n >> BigInt(index * 8)) & 255n))
            .reverse());
        if (n < 256) {
            return {
                tag: 2 ** tagWidth - 4,
                additionalBytes: bytes.slice(bytes.length - 1),
            };
        }
        else if (n < 256 ** 2) {
            return {
                tag: 2 ** tagWidth - 3,
                additionalBytes: bytes.slice(bytes.length - 2),
            };
        }
        else if (n < 256 ** 4) {
            return {
                tag: 2 ** tagWidth - 2,
                additionalBytes: bytes.slice(bytes.length - 4),
            };
        }
        else {
            return {
                tag: 2 ** tagWidth - 1,
                additionalBytes: bytes,
            };
        }
    }
    static decodeCompactAdditionalBytesLength(tag, tagWidth) {
        switch (2 ** tagWidth - tag) {
            case 1:
                return 8;
            case 2:
                return 4;
            case 3:
                return 2;
            case 4:
                return 1;
            default:
                return 0;
        }
    }
    static decodeCompactAdditionalBytes(bytes) {
        return bytes
            .reverse()
            .reduce((previousValue, currentValue, index) => previousValue | (BigInt(currentValue) << BigInt(index * 8)), 0n);
    }
}
export const UInt64 = UInt64Constructor;
//# sourceMappingURL=UInt64.js.map